import _ from 'lodash'
import type { CanvasEngineConstructor } from './types'
import { CanvasEngine } from './index'
import type { ShapeForDocs } from '@/app/data/type'

/**
 * 가상에서 렌더링 한 다음, 이미지를 추출하거나 index를 추출하는 역할.
 */
export class CanvasEngineRender extends CanvasEngine {
  public static prepare(arg: CanvasEngineConstructor): CanvasEngineRender {
    const canvas = new CanvasEngineRender(arg)
    return canvas
  }

  constructor(arg: CanvasEngineConstructor) {
    super(arg)
    if (arg.container instanceof HTMLDivElement) {
      this.virtualDivElement = arg.container
    }
  }

  protected virtualDivElement: HTMLDivElement | undefined
  importRenderLayers(layers: { name: string; id: string }[]): void {
    for (const layer of layers) {
      this.createLayer(layer.name, layer.id)
    }
  }

  /**
   * 렌더링용 배경 이미지 설정
   * @param imageUrl 배경 이미지 URL
   * @param rotate 회전 각도 (0, 90, 180, 270)
   */
  async setRenderBackground(imageUrl: string, rotate: number = 0): Promise<void> {
    if (!imageUrl) return
    if (!this.getBackgroundLayer()) {
      throw new Error('배경 레이어를 찾을 수 없습니다.')
    }
    const bgLayer = this.renderer.layers.background
    const htmlImageElement = new Image()
    htmlImageElement.crossOrigin = 'Anonymous'
    return new Promise((resolve, _) => {
      htmlImageElement.onload = (): void => {
        const background = this.renderer.createImage({
          x: 0,
          y: 0,
          image: htmlImageElement,
          draggable: false,
        })
        this.backgroundWidth = background.width()
        this.backgroundHeight = background.height()
        this.averageBackgroundSize = (this.backgroundWidth + this.backgroundHeight) / 2
        this.events.onBackgroundSizeChanged(this.backgroundWidth, this.backgroundHeight)

        // 모든 엔티티의 cloud border 재생성
        this.entities.forEach(entity => {
          entity.onBackgroundSizeChanged()
        })

        bgLayer.add(background)
        this.renderer.drawNow(bgLayer)
        if (this.backgroundImage) {
          this.backgroundImage.destroy()
        }
        this.backgroundImage = background

        // 회전 적용
        background.rotate(rotate)
        this.backgroundRotation = rotate as 0 | 90 | 180 | 270
        if (rotate == 90) {
          background.x(this.backgroundHeight)
          background.y(0)
        } else if (rotate == 180) {
          background.x(this.backgroundWidth)
          background.y(this.backgroundHeight)
        } else if (rotate == 270) {
          background.x(0)
          background.y(this.backgroundWidth)
        }

        background.moveToBottom()
        if (this.virtualDivElement) {
          // 회전에 따라 너비/높이 조정
          let finalWidth = this.backgroundWidth
          let finalHeight = this.backgroundHeight
          if (rotate === 90 || rotate === 270) {
            finalWidth = this.backgroundHeight
            finalHeight = this.backgroundWidth
          }
          this.virtualDivElement.style.width = finalWidth + 'px'
          this.virtualDivElement.style.height = finalHeight + 'px'
          this.renderer.stage.width(finalWidth)
          this.renderer.stage.height(finalHeight)
        }
        resolve()
      }
      htmlImageElement.src = imageUrl
    })
  }

  /**
   * 도면을 이미지 랜더링
   * 반드시 `importEntities()`와 `importLayers()`를 실행한 후 호출
   * @param shapes 도형 목록
   */
  async render(shapes: ShapeForDocs[]): Promise<string | Blob> {
    // 엔티티 정렬
    this.sortEntities(shapes.map(shape => String(shape.id)))

    this.entities.forEach(entity => {
      const shape = shapes.find(shape => entity.getId() === String(shape.id))
      const balloonText = shape?.exportIndex
      if (balloonText) entity.setBalloonText(String(balloonText))
      entity.ungroupBalloon()
    })

    if (!this.getBackgroundLayer()) {
      throw new Error('배경 레이어를 찾을 수 없습니다.')
    }
    this.renderer.layers.background.moveToBottom()

    // 렌더링 된 이미지를 추출
    const image = await this.toJpegFile()

    return image
  }

  /**
   * 렌더링 중 사용한 모든 리소스를 해제
   */
  release(): void {
    this.resetAllLayer()
    this.entities.forEach(entity => {
      entity.node.destroy()
    })
    this.entities = []

    //  시스템 레이어의 모든 항목 삭제
    this.renderer.layers.system.removeChildren()
    this.updateIndex()
    this.renderer.drawNow()
  }
}
